import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { TbUser, TbUsers, TbSearch, TbSend, TbX, TbTrash } from "react-icons/tb";
import { PiHandArrowDown } from "react-icons/pi";
import { apiRequest } from "src/services/apiClient";
import { config } from "configs/config";
import { sanitizeInput, trimInput, normalizePlz } from "src/helpers/sanitizeInput";
import { isMinorFromIso } from "src/services/gdtParser";
import type { PendingGdtReviewItem } from "src/context/PraxisSoftwareSyncContext";
import type { GdtRecipientResolution, GdtRevertInfo, GdtSyncResult } from "src/services/gdtSyncService";
import "../CreateRecipientModal/createRecipientModal.scss";
import "../../pages/PatientPage/changeRecipientPopup.scss";
import "./gdtPatientReviewPopup.scss";

interface RecipientOption {
    id: string;
    firstName?: string;
    lastName: string;
    email?: string;
}

type RecipientMode = "pick" | "create";
type Phase = "recipient" | "review";

function formatName(r: RecipientOption) {
    return [r.firstName, r.lastName].filter(Boolean).join(" ");
}

/** Shown once per parsed .gdt file — by the time this mounts, the patient (and, wherever
 *  it could be determined, its billing recipient) is already committed server-side (see
 *  processOneFile in PraxisSoftwareSyncContext.tsx). This is a confirmation surface, not a
 *  gate: it shows what was read from the file plus a ready QR code, with two actions (Add,
 *  Add and send Einwilligung). The only case still needing staff input first is a minor
 *  with no recipient identifiable from the file at all (`item.result.needsRecipient`),
 *  which falls back to a pick/create-recipient step before a QR can exist. */
export function GdtPatientReviewPopup({
    item,
    onResolveRecipient,
    onSendConsent,
    onCancel,
    onAdvance,
}: {
    item: PendingGdtReviewItem;
    onResolveRecipient: (recipientResolution: GdtRecipientResolution) => Promise<GdtSyncResult | null>;
    onSendConsent: (recipientId: string) => Promise<boolean>;
    onCancel: (patientId: string, revertHistory: GdtRevertInfo[]) => Promise<void>;
    onAdvance: () => void;
}) {
    const { t } = useTranslation();
    const { parsed, filename, result } = item;

    const [committed, setCommitted] = useState<GdtSyncResult>(result);
    const [phase, setPhase] = useState<Phase>(result.needsRecipient ? "recipient" : "review");
    // Every write made for this file so far (the initial commit, plus a second entry if
    // staff resolved a fallback recipient) — Cancel undoes all of them, not just the latest,
    // since the fallback path's own revertInfo only covers its own delta (see cancelGdtSync
    // in PraxisSoftwareSyncContext.tsx).
    const [revertHistory, setRevertHistory] = useState<GdtRevertInfo[]>([result.revertInfo]);
    const [cancelling, setCancelling] = useState(false);

    const handleCancel = async () => {
        setCancelling(true);
        await onCancel(committed.patientId, revertHistory);
    };

    const isMinor = isMinorFromIso(parsed.birthDate);
    const isExistingPatient = !result.created;

    // ── Fallback recipient picker — only reached when committed.needsRecipient ─────────
    const [recipientMode, setRecipientMode] = useState<RecipientMode>(parsed.guardian ? "create" : "pick");
    const [recipients, setRecipients] = useState<RecipientOption[]>([]);
    const [recipientsLoading, setRecipientsLoading] = useState(false);
    const [recipientSearch, setRecipientSearch] = useState("");
    const [selectedRecipient, setSelectedRecipient] = useState<"self" | string | null>(null);
    const [newRecSalutation, setNewRecSalutation] = useState(parsed.guardian?.gender === "F" ? "F" : "H");
    const [newRecTitle, setNewRecTitle] = useState(parsed.guardian?.title ?? "");
    const [newRecFirstName, setNewRecFirstName] = useState(parsed.guardian?.firstName ?? "");
    const [newRecLastName, setNewRecLastName] = useState(parsed.guardian?.lastName ?? "");
    // A minor's own "email" field (parsed.email) commonly carries the guardian's real contact
    // email in practice, so it's a sensible default here too — staff can still edit or clear it.
    const [newRecEmail, setNewRecEmail] = useState(parsed.guardian ? (parsed.email ?? "") : "");
    const [newRecStreet, setNewRecStreet] = useState(parsed.guardian?.street ?? "");
    const [newRecPlz, setNewRecPlz] = useState(parsed.guardian?.plz ?? "");
    const [newRecCity, setNewRecCity] = useState(parsed.guardian?.city ?? "");
    const [resolving, setResolving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (phase !== "recipient") return;
        setRecipientsLoading(true);
        apiRequest<RecipientOption[]>("/recipients")
            .then((d) => setRecipients(Array.isArray(d) ? d : []))
            .catch(() => {})
            .finally(() => setRecipientsLoading(false));
    }, [phase]);

    const filteredRecipients = useMemo(() => {
        const q = recipientSearch.toLowerCase();
        return recipients.filter((r) => `${r.firstName ?? ""} ${r.lastName} ${r.email ?? ""}`.toLowerCase().includes(q));
    }, [recipients, recipientSearch]);

    const recipientChosen = recipientMode === "create" ? !!newRecLastName.trim() : !!selectedRecipient;

    const handleContinue = async () => {
        setError(null);
        if (!recipientChosen) { setError(t("praxisSoftwareSync.reviewRecipientRequired")); return; }
        const resolution: GdtRecipientResolution = recipientMode === "create"
            ? {
                mode: "new",
                newRecipient: {
                    salutation: newRecSalutation,
                    title: newRecTitle || undefined,
                    firstName: newRecFirstName || undefined,
                    lastName: newRecLastName,
                    email: newRecEmail || undefined,
                    street: newRecStreet || undefined,
                    plz: newRecPlz || undefined,
                    city: newRecCity || undefined,
                    // The file's guardian birth date, unless staff typed in someone else.
                    birthDate: newRecLastName === parsed.guardian?.lastName && newRecFirstName === (parsed.guardian?.firstName ?? "")
                        ? parsed.guardian?.birthDate
                        : undefined,
                },
            }
            : selectedRecipient === "self"
                ? { mode: "self" }
                : { mode: "existing", recipientId: selectedRecipient as string };

        setResolving(true);
        const updated = await onResolveRecipient(resolution);
        setResolving(false);
        if (!updated) { setError(t("praxisSoftwareSync.unknownError")); return; }
        setCommitted(updated);
        setRevertHistory((prev) => [...prev, updated.revertInfo]);
        setPhase("review");
    };

    // ── Review phase — patient summary + an automatically-fetched QR ───────────────────
    const [qrLoading, setQrLoading] = useState(false);
    const [qrUrl, setQrUrl] = useState<string | null>(null);
    const [qrError, setQrError] = useState<string | null>(null);
    // Recipient is post-only (backend utils/postOnly.ts) — the QR endpoint refuses, and so would
    // the consent email, so neither is offered; only paper consent applies.
    const [postOnly, setPostOnly] = useState(false);
    const [sendingConsent, setSendingConsent] = useState(false);

    useEffect(() => {
        if (phase !== "review" || !committed.recipientId) return;
        let cancelled = false;
        setQrLoading(true);
        setQrError(null);
        setPostOnly(false);
        (async () => {
            try {
                const token = localStorage.getItem("token");
                const praxisId = JSON.parse(localStorage.getItem("praxis") || "null")?.id;
                const doctorId = JSON.parse(localStorage.getItem("doctor") || "null")?.id;
                const res = await fetch(`${config.baseUrl}/api/patient-portal/questionnaire/create-qr`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        ...(token && { Authorization: `Bearer ${token}` }),
                        ...(doctorId && { "X-Doctor-Id": doctorId }),
                        ...(praxisId && { "X-Praxis-Id": praxisId }),
                    },
                    body: JSON.stringify({ recipientId: committed.recipientId }),
                });
                if (res.status === 403) {
                    const body = await res.json().catch(() => null);
                    if (body?.message === "POST_ONLY") {
                        if (!cancelled) setPostOnly(true);
                        return;
                    }
                }
                if (!res.ok) throw new Error("Failed");
                const blob = await res.blob();
                if (!cancelled) setQrUrl(URL.createObjectURL(blob));
            } catch {
                if (!cancelled) setQrError(t("praxisSoftwareSync.reviewQrFailed"));
            } finally {
                if (!cancelled) setQrLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase, committed.recipientId]);

    const addLabel = isExistingPatient ? t("praxisSoftwareSync.reviewSave") : t("praxisSoftwareSync.reviewAdd");
    const addConsentLabel = isExistingPatient ? t("praxisSoftwareSync.reviewSaveConsentBtn") : t("praxisSoftwareSync.reviewAddConsentBtn");

    const handleSendConsent = async () => {
        if (!committed.recipientId) { onAdvance(); return; }
        setSendingConsent(true);
        await onSendConsent(committed.recipientId);
        setSendingConsent(false);
        onAdvance();
    };

    const recipientDisplayName = committed.recipientLastName
        ? [committed.recipientFirstName, committed.recipientLastName].filter(Boolean).join(" ")
        : null;

    const patientAddress = [parsed.street, [parsed.plz, parsed.city].filter(Boolean).join(" ")]
        .filter(Boolean).join(", ");

    return createPortal(
        <div className="create-modal-overlay">
            <div className="create-modal gdt-review-modal">
                <h2 className="create-modal__title">
                    {isExistingPatient ? t("praxisSoftwareSync.reviewTitleUpdate") : t("praxisSoftwareSync.reviewTitleNew")}
                </h2>
                {isExistingPatient && (
                    <span className="gdt-review-modal__already-badge">{t("praxisSoftwareSync.reviewAlreadyImportedBadge")}</span>
                )}
                <p className="gdt-review-modal__filename">{filename}</p>

                <div className="gdt-review-modal__patient-summary">
                    <strong>{t("praxisSoftwareSync.reviewPatientSummaryHeading")}</strong>
                    <div className="gdt-review-modal__patient-summary-grid">
                        <span>{[parsed.firstName, parsed.lastName].filter(Boolean).join(" ")}</span>
                        {parsed.birthDate && <span>{parsed.birthDate}</span>}
                        {patientAddress && <span>{patientAddress}</span>}
                        {parsed.email && <span>{parsed.email}</span>}
                        {!!parsed.tel?.length && <span>{parsed.tel.join(", ")}</span>}
                    </div>
                </div>

                {phase === "recipient" && (
                    <div className="create-modal__form">
                        <div className="create-modal__billing-options">
                            <button
                                type="button"
                                className={`create-modal__billing-opt${recipientMode === "pick" && selectedRecipient === "self" ? " create-modal__billing-opt--active" : ""}`}
                                disabled={isMinor}
                                title={isMinor ? t("praxisSoftwareSync.reviewSelfDisabledMinor") : undefined}
                                onClick={() => { setRecipientMode("pick"); setSelectedRecipient("self"); }}
                            >
                                <span className="create-modal__billing-opt-icon"><TbUser /></span>
                                <span className="create-modal__billing-opt-text">
                                    <strong>{t("createPatient.recipientOptionSelf")}</strong>
                                    <small>{isMinor ? t("praxisSoftwareSync.reviewSelfDisabledMinor") : t("createPatient.recipientOptionSelfDesc")}</small>
                                </span>
                            </button>
                            <button
                                type="button"
                                className={`create-modal__billing-opt${recipientMode === "create" ? " create-modal__billing-opt--active" : ""}`}
                                onClick={() => { setRecipientMode("create"); setSelectedRecipient(null); }}
                            >
                                <span className="create-modal__billing-opt-icon"><TbUsers /></span>
                                <span className="create-modal__billing-opt-text">
                                    <strong>{t("createPatient.recipientOptionOther")}</strong>
                                    <small>{t("createPatient.recipientOptionOtherDesc")}</small>
                                </span>
                            </button>
                        </div>

                        {recipientMode === "pick" && selectedRecipient !== "self" && (
                            <div className="gdt-review-modal__picker">
                                <div className="crp-search">
                                    <TbSearch className="crp-search__icon" />
                                    <input
                                        className="crp-search__input"
                                        placeholder={t("patientPage.changeRecipientSearch")}
                                        value={recipientSearch}
                                        onChange={(e) => setRecipientSearch(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                                <div className="crp-list">
                                    {recipientsLoading && <div className="crp-state">…</div>}
                                    {!recipientsLoading && filteredRecipients.length === 0 && (
                                        <div className="crp-state">{t("patientPage.noRecipient")}</div>
                                    )}
                                    {filteredRecipients.map((r) => (
                                        <button
                                            key={r.id}
                                            type="button"
                                            className={`crp-option${selectedRecipient === r.id ? " crp-option--active" : ""}`}
                                            onClick={() => setSelectedRecipient(selectedRecipient === r.id ? null : r.id)}
                                        >
                                            <div className="crp-option__icon"><PiHandArrowDown /></div>
                                            <div className="crp-option__text">
                                                <strong>{formatName(r)}</strong>
                                                {r.email && <span>{r.email}</span>}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {recipientMode === "create" && (
                            <>
                                <div className="create-modal__row">
                                    <div className="create-modal__field">
                                        <label>{t("form.salutation")}</label>
                                        <select value={newRecSalutation} onChange={(e) => setNewRecSalutation(e.target.value)}>
                                            <option value="H">{t("form.herr")}</option>
                                            <option value="F">{t("form.frau")}</option>
                                        </select>
                                    </div>
                                    <div className="create-modal__field">
                                        <label>{t("form.title")}</label>
                                        <input value={newRecTitle} maxLength={20} placeholder={t("form.drPlaceholder")} onChange={(e) => setNewRecTitle(sanitizeInput(e.target.value))} onBlur={(e) => setNewRecTitle(trimInput(e.target.value))} />
                                    </div>
                                </div>
                                <div className="create-modal__row">
                                    <div className="create-modal__field">
                                        <label>{t("form.firstName")}</label>
                                        <input value={newRecFirstName} maxLength={50} onChange={(e) => setNewRecFirstName(sanitizeInput(e.target.value))} onBlur={(e) => setNewRecFirstName(trimInput(e.target.value))} />
                                    </div>
                                    <div className="create-modal__field">
                                        <label>{t("form.lastName")} *</label>
                                        <input value={newRecLastName} maxLength={50} onChange={(e) => setNewRecLastName(sanitizeInput(e.target.value))} onBlur={(e) => setNewRecLastName(trimInput(e.target.value))} />
                                    </div>
                                </div>
                                <div className="create-modal__row">
                                    <div className="create-modal__field">
                                        <label>{t("form.email")}</label>
                                        <input type="email" value={newRecEmail} onChange={(e) => setNewRecEmail(sanitizeInput(e.target.value))} onBlur={(e) => setNewRecEmail(trimInput(e.target.value))} />
                                    </div>
                                    <div className="create-modal__field">
                                        <label>{t("form.street")}</label>
                                        <input value={newRecStreet} onChange={(e) => setNewRecStreet(sanitizeInput(e.target.value))} onBlur={(e) => setNewRecStreet(trimInput(e.target.value))} />
                                    </div>
                                </div>
                                <div className="create-modal__row">
                                    <div className="create-modal__field create-modal__field--short">
                                        <label>{t("form.plz")}</label>
                                        <input value={newRecPlz} onChange={(e) => setNewRecPlz(normalizePlz(e.target.value))} />
                                    </div>
                                    <div className="create-modal__field">
                                        <label>{t("form.city")}</label>
                                        <input value={newRecCity} onChange={(e) => setNewRecCity(sanitizeInput(e.target.value))} onBlur={(e) => setNewRecCity(trimInput(e.target.value))} />
                                    </div>
                                </div>
                            </>
                        )}

                        {error && <p className="create-modal__error">{error}</p>}

                        <div className="create-modal__actions">
                            <button type="button" className="create-modal__btn create-modal__btn--danger" disabled={resolving || cancelling} onClick={() => void handleCancel()}>
                                <TbTrash /> {cancelling ? t("createPatient.creating") : t("praxisSoftwareSync.reviewCancel")}
                            </button>
                            <button type="button" className="create-modal__btn create-modal__btn--cancel" disabled={resolving || cancelling} onClick={onAdvance}>
                                <TbX /> {t("praxisSoftwareSync.reviewRecipientLater")}
                            </button>
                            <button type="button" className="create-modal__btn create-modal__btn--submit" disabled={resolving || cancelling} onClick={() => void handleContinue()}>
                                {resolving ? t("createPatient.creating") : t("praxisSoftwareSync.reviewContinue")}
                            </button>
                        </div>
                    </div>
                )}

                {phase === "review" && (
                    <div className="create-modal__qr-screen">
                        {recipientDisplayName && <p className="gdt-review-modal__recipient-line">{recipientDisplayName}</p>}
                        {item.agentCommit && (
                            <p className="gdt-review-modal__agent-note" role="status">
                                {t("praxisSoftwareSync.reviewAgentProcessed", {
                                    time: new Date(item.agentCommit.committedAt).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }),
                                })}
                            </p>
                        )}
                        <p className="create-modal__lead">
                            {postOnly ? t("recipientPage.postOnlyConsentHint") : t("praxisSoftwareSync.reviewQrLead")}
                        </p>
                        {qrLoading && <div className="create-modal__qr-loading gdt-review-modal__qr-spinner" />}
                        {!qrLoading && qrUrl && <img className="create-modal__qr-img" src={qrUrl} alt={t("praxisSoftwareSync.reviewQrTitle")} />}
                        {!qrLoading && qrError && <p className="create-modal__error">{qrError}</p>}
                        <div className="create-modal__actions">
                            <button
                                type="button"
                                className="create-modal__btn create-modal__btn--danger"
                                disabled={sendingConsent || cancelling}
                                onClick={() => void handleCancel()}
                            >
                                <TbTrash /> {cancelling ? t("createPatient.creating") : t("praxisSoftwareSync.reviewCancel")}
                            </button>
                            {!postOnly && (
                                <button
                                    type="button"
                                    className="create-modal__btn gdt-review-modal__btn--consent"
                                    disabled={sendingConsent || cancelling}
                                    onClick={() => void handleSendConsent()}
                                >
                                    <TbSend /> {sendingConsent ? t("createPatient.creating") : addConsentLabel}
                                </button>
                            )}
                            <button
                                type="button"
                                className="create-modal__btn create-modal__btn--submit"
                                disabled={sendingConsent || cancelling}
                                onClick={onAdvance}
                            >
                                {addLabel}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>,
        document.body,
    );
}
